/**
 * Pastebin - Zwischenspeicher JE BENUTZER (VDI <-> PC). Kein geteilter Slot mehr: der
 * Inhalt eines Benutzers ist fuer andere nicht lesbar.
 *
 * Versteckte Seite (kein Sidebar-Eintrag), erreichbar nur direkt über
 * /pastebin. Genau EIN Slot JE BENUTZER: Speichern überschreibt den vorigen
 * Inhalt desselben Benutzers. Backend: slots[req.userId] in GET und PUT
 * (server.js ~13467/13471) - kein globaler Slot.
 * Gedacht für Groovy-Scripts: Editor mit Syntax-Highlighting
 * (transparente Textarea über eingefärbtem Code, scroll-synchron).
 */
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../utils/helpers";
import { useToast, ToastContainer } from "../components/ui";

/* ── Groovy-Highlighter (zeilenweise, mit Blockkommentar-Status) ── */
const KW = /\b(def|return|if|else|for|while|try|catch|finally|throw|new|import|package|class|null|true|false|void|in|as|instanceof|extends|implements|interface|abstract|final|static|private|public|protected|switch|case|break|continue|default|assert|this|super|enum|do)\b/;
const CPI = /\b(message|body|headers|properties|exchange|camelContext)\b/;
const NUM = /\b(0x[0-9a-fA-F]+|\d+\.?\d*[fFdDlL]?)\b/;
const STR = /("""[\s\S]*?"""|'''[\s\S]*?'''|"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/;
const ANNO = /(@[A-Za-z_][\w]*)/;

function tokenizeLine(line, inBlock) {
  // Rückgabe: { parts: [{t:"kw|str|com|num|cpi|anno|plain", s:text}], inBlock }
  const parts = [];
  let rest = line;
  while (rest.length) {
    if (inBlock) {
      const end = rest.indexOf("*/");
      if (end < 0) { parts.push({ t: "com", s: rest }); rest = ""; break; }
      parts.push({ t: "com", s: rest.slice(0, end + 2) }); rest = rest.slice(end + 2); inBlock = false; continue;
    }
    // Nächstes Spezial-Token finden
    const candidates = [];
    const lc = rest.indexOf("//"); if (lc >= 0) candidates.push([lc, "lcom"]);
    const bc = rest.indexOf("/*"); if (bc >= 0) candidates.push([bc, "bcom"]);
    const sm = rest.match(STR); if (sm) candidates.push([sm.index, "str", sm[0]]);
    const am = rest.match(ANNO); if (am) candidates.push([am.index, "anno", am[0]]);
    if (!candidates.length) { pushWords(parts, rest); rest = ""; break; }
    candidates.sort((a, b) => a[0] - b[0]);
    const [idx, kind, tok] = candidates[0];
    if (idx > 0) pushWords(parts, rest.slice(0, idx));
    if (kind === "lcom") { parts.push({ t: "com", s: rest.slice(idx) }); rest = ""; }
    else if (kind === "bcom") {
      const end = rest.indexOf("*/", idx + 2);
      if (end < 0) { parts.push({ t: "com", s: rest.slice(idx) }); rest = ""; inBlock = true; }
      else { parts.push({ t: "com", s: rest.slice(idx, end + 2) }); rest = rest.slice(end + 2); }
    }
    else if (kind === "str") { parts.push({ t: "str", s: tok }); rest = rest.slice(idx + tok.length); }
    else { parts.push({ t: "anno", s: tok }); rest = rest.slice(idx + tok.length); }
  }
  return { parts, inBlock };
}
function pushWords(parts, text) {
  // Keywords / CPI-Variablen / Zahlen im Resttext markieren
  let rest = text;
  while (rest.length) {
    const k = rest.match(KW), c = rest.match(CPI), n = rest.match(NUM);
    const hits = [k && [k.index, "kw", k[0]], c && [c.index, "cpi", c[0]], n && [n.index, "num", n[0]]].filter(Boolean);
    if (!hits.length) { parts.push({ t: "plain", s: rest }); break; }
    hits.sort((a, b) => a[0] - b[0]);
    const [idx, t, tok] = hits[0];
    if (idx > 0) parts.push({ t: "plain", s: rest.slice(0, idx) });
    parts.push({ t, s: tok });
    rest = rest.slice(idx + tok.length);
  }
}

function HighlightedCode({ code }) {
  const lines = useMemo(() => {
    const out = [];
    let inBlock = false;
    for (const line of code.split("\n")) {
      const r = tokenizeLine(line, inBlock);
      out.push(r.parts);
      inBlock = r.inBlock;
    }
    return out;
  }, [code]);
  return <>
    {lines.map((parts, i) => (
      <div className="iio-pb-line" key={i}>
        <span className="iio-pb-ln">{i + 1}</span>
        <span className="iio-pb-code">
          {parts.length === 0 ? " " : parts.map((p, j) => <span key={j} className={"tk-" + p.t}>{p.s}</span>)}
        </span>
      </div>
    ))}
  </>;
}

const PastebinPage = () => {
  const { t, i18n } = useTranslation(["pastebin", "gemeinsam"]);
  const gebiet = i18n.language === "en" ? "en-GB" : "de-DE";
  // t liegt in einem Ref, damit ein Sprachwechsel weder den Abrufrhythmus
  // noch die Tastaturbindung neu aufsetzt.
  const tRef = useRef(t);
  tRef.current = t;
  const { toasts, addToast } = useToast();
  const [content, setContent] = useState("");
  const [meta, setMeta] = useState({ updatedAt: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const taRef = useRef(null);
  const preRef = useRef(null);

  const load = useCallback(async (silent) => {
    try {
      const d = await api("/api/pastebin");
      // Lokale, ungespeicherte Änderungen nie überschreiben
      if (!dirtyRef.current) {
        setContent(d.content || "");
        setMeta({ updatedAt: d.updatedAt });
      }
    } catch (e) { if (!silent) addToast(e.message, "error"); }
    setLoading(false);
  }, [addToast]);

  useEffect(() => {
    load(false);
    const t = setInterval(() => load(true), 10000);
    return () => clearInterval(t);
  }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const r = await api("/api/pastebin", { method: "PUT", body: JSON.stringify({ content }) });
      setMeta({ updatedAt: r.updatedAt });
      setDirty(false);
      addToast(tRef.current("meldung.gespeichert"), "success");
    } catch (e) { addToast(e.message, "error"); }
    setSaving(false);
  }, [content, addToast]);

  // Strg+S speichert
  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") { e.preventDefault(); save(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [save]);

  const copyAll = async () => {
    try { await navigator.clipboard.writeText(content); addToast(t("meldung.kopiert"), "success"); }
    catch { addToast(t("meldung.kopierenFehlgeschlagen"), "error"); }
  };

  const onScroll = () => {
    if (preRef.current && taRef.current) {
      preRef.current.scrollTop = taRef.current.scrollTop;
      preRef.current.scrollLeft = taRef.current.scrollLeft;
    }
  };
  const onKeyDown = (e) => {
    // Tab rückt ein statt Fokus zu wechseln
    if (e.key === "Tab") {
      e.preventDefault();
      const ta = e.target, s = ta.selectionStart, en = ta.selectionEnd;
      const next = content.slice(0, s) + "    " + content.slice(en);
      setContent(next); setDirty(true);
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s + 4; });
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString(gebiet, { day: "2-digit", month: "2-digit", year: "2-digit" }) + ", " + d.toLocaleTimeString(gebiet, { hour: "2-digit", minute: "2-digit" });
  };
  const lineCount = content === "" ? 1 : content.split("\n").length;

  return <div className="iio-pb">
    <ToastContainer toasts={toasts}/>

    <div className="iio-page-head">
      <div className="iio-page-head-left iio-anim-arrival">
        <div className="iio-breadcrumb">
          <span className="iio-accent">{t("kopf.bereich")}</span>
          <span className="iio-sep">/</span>
          <span>{t("kopf.seite")}</span>
        </div>
        <h1>{t("kopf.titel")}</h1>
        <p className="iio-lead">
          {t("kopf.lead")}
        </p>
      </div>
      <div className="iio-page-head-right iio-anim-arrival" style={{ animationDelay: "80ms" }}>
        <button className="iio-btn iio-btn--secondary" onClick={copyAll} disabled={!content}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          {t("aktion.kopieren")}
        </button>
        <button className="iio-btn iio-btn--signal" onClick={save} disabled={saving || !dirty}>
          {saving ? t("aktion.speichertLaeuft") : t("aktion.speichern")}
        </button>
      </div>
    </div>

    <div className="iio-pb-bar iio-anim-arrival" style={{ animationDelay: "120ms" }}>
      <span className={"iio-status " + (dirty ? "iio-status--warn" : "iio-status--ok")}>
        {dirty ? t("leiste.ungespeichert") : t("leiste.synchron")}
      </span>
      <span className="iio-pb-meta">
        {meta.updatedAt ? t("leiste.zuletztGespeichert", { zeit: fmtTime(meta.updatedAt) }) : t("leiste.nochNichts")}
      </span>
      <span className="iio-pb-meta right">{t("leiste.zeilen", { count: lineCount })} · {t("leiste.zeichen", { count: content.length, anzahl: content.length.toLocaleString(gebiet) })} · {t("leiste.tastenkuerzel")}</span>
    </div>

    <div className="iio-pb-editor iio-anim-arrival" style={{ animationDelay: "160ms" }}>
      {loading ? (
        <div className="iio-pb-loading">{t("editor.laedt")}</div>
      ) : <>
        <div className="iio-pb-pre" ref={preRef} aria-hidden="true">
          <HighlightedCode code={content}/>
        </div>
        <textarea
          ref={taRef}
          className="iio-pb-ta"
          value={content}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          wrap="off"
          placeholder={t("editor.platzhalter")}
          onChange={e => { setContent(e.target.value); setDirty(true); }}
          onScroll={onScroll}
          onKeyDown={onKeyDown}
        />
      </>}
    </div>
  </div>;
};

export default PastebinPage;
